import classNames from 'classnames';
import isFunction from 'lodash/isFunction';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MaskedInput from 'react-text-mask';
import { Controller } from 'react-hook-form';

import { maskedInputConvertMask } from 'src/components/UI/inputs/CleanMaskedInput';
import { getInputError } from 'src/components/UI/inputs/common';
import { FormPropsType } from 'src/components/UI/types';
import { Validate } from 'react-hook-form/dist/types/validator';
import { uiMemoFormPropsInputCompareFn } from 'src/components/UI/UIMemoFormPropsCompareFn';
import { inputBaseHook } from 'src/utils/hooks/inputs';
import { useHoverErrors } from 'src/components/UI/inputs/useHoverErrors';
import { InputMaterialWrapper } from 'src/components/UI/inputs/common/InputMaterialWrapper';
import { SafeAnyType } from 'src/utils/safeAny';

// eslint-disable-next-line no-underscore-dangle
const _label = 'The field';

type Props = {
  formProps: FormPropsType;
  name: string;
  mask: string; // mask in a string

  className?: string;
  placeholder?: string;
  required?: boolean;
  requiredMessage?: string;
  errorLabel?: string;
  validate?: Validate<string>; // the function to validate value (not blocking an input)
  disabled?: boolean;
  material?: boolean;
  autoFocus?: boolean;
};

export const UiMaskedInput: React.FC<Props> = memo(
  ({
    formProps: {
      clearErrors,
      control,
      formState: { errors },
      getValues,
    },
    name,
    className,
    required,
    requiredMessage,
    errorLabel,
    validate,
    placeholder,
    mask,
    disabled,
    material,
    autoFocus = false,
  }: Props) => {
    const { t } = useTranslation();

    const [active, setActive] = useState(false);
    const [hasValue, setHasValue] = useState(!!getValues(name));

    const mountRef = useRef(false);

    useEffect(() => inputBaseHook(mountRef, name, clearErrors), [clearErrors, name]);

    const error = getInputError(errors, name);

    const arrMask = useMemo(() => maskedInputConvertMask(mask), [mask]);

    const { hoverMessage, handleChangeInputHover } = useHoverErrors({
      name,
      clearErrors,
      error,
      showAnyError: material,
    });

    const handleFocus = useCallback(() => {
      if (!disabled) {
        setActive(true);
      }
    }, [disabled]);

    const render = useCallback(
      ({ field }: SafeAnyType) => (
        <MaskedInput
          mask={arrMask}
          type="text"
          disabled={disabled}
          className={classNames(className, {
            error: !!error,
          })}
          autoFocus={autoFocus}
          placeholder={material ? undefined : placeholder}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...field}
          onChange={(e) => {
            field.onChange(e);

            setHasValue(!!e.target.value);

            handleChangeInputHover();
          }}
          onBlur={() => {
            field.onBlur();

            if (!disabled) {
              setActive(false);
            }
          }}
          onFocus={handleFocus}
        />
      ),
      [arrMask, autoFocus, className, disabled, error, handleChangeInputHover, handleFocus, material, placeholder]
    );

    const rules = useMemo(
      () => ({
        validate: (v?: string | null) => {
          if (mountRef.current) {
            if ((required && !v) || v?.includes('_')) {
              return (
                requiredMessage || t('common.validation.required', { label: errorLabel || name || _label }).toString()
              );
            }

            if (isFunction(validate)) {
              return validate(v as string);
            }
          }

          return undefined;
        },
      }),
      [errorLabel, name, required, requiredMessage, t, validate]
    );

    return material ? (
      <InputMaterialWrapper
        active={active}
        disabled={disabled}
        hasErrors={!!hoverMessage}
        errorMessage={hoverMessage}
        placeholder={placeholder}
        hasValue={hasValue}
      >
        <Controller render={render} control={control} name={name} rules={rules} />
      </InputMaterialWrapper>
    ) : (
      <Controller render={render} control={control} name={name} rules={rules} />
    );
  },
  uiMemoFormPropsInputCompareFn
);
